import {
  type Bounds,
  type BoxId,
  type EdgeId,
  type GraphPath,
  type GraphPathInfo,
  NODE_BOX_BORDER_RADIUS,
  NODE_BOX_LABEL_FONT,
  NODE_BOX_LABEL_FONT_SIZE,
  NODE_BOX_LABEL_PADDING,
  NODE_BOX_LINE_ALPHA,
  NODE_BOX_LINE_COLOR,
  NODE_BOX_LINE_WIDTH,
  type NodeId,
  bounds,
  brand,
  getEdgeBoxId,
  getNodeBoxPositions,
  getRecordEntries,
} from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/function';
import {
  BitmapText,
  Container,
  Graphics,
  type IHitArea,
  Sprite,
  Texture,
  type interaction,
} from 'pixi.js';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { fitToViewport } from 'components/Graph/GraphApp/helpers/fitToViewport';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import {
  ZINDEX_BOX_BASE,
  ZINDEX_BOX_DRAG,
} from 'components/Graph/GraphApp/zIndexes';
import {
  type TopologyGraphMetadata,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { hideTooltip } from 'components/Graph/commands/pathLookup/hideTooltip';
import { showLayoutButtonTooltip } from 'components/Graph/commands/pathLookup/showLayoutButtonTooltip';
import { showGraphLayoutDialog } from 'components/Graph/commands/showGraphLayoutDialog';
import { isFallbackLayout } from 'components/Graph/helpers/isFallbackLayout';
import { getGraphResources } from 'components/Graph/resources/resources';
import { defineReadonlyAccessor } from 'utils/object/defineReadonlyAccessor';
import { boundsToPIXIRectangle } from 'utils/pixi/Bounds';
import {
  BoundsCalculator,
  MergedBoundsCalculator,
} from 'utils/pixi/BoundsCalculator';
import { getBitmapTextAnchor } from 'utils/pixi/getBitmapTextAnchor';

/**
 * null means no layouting (e.g. for pathlookup boxes)
 */
type LayoutState = {
  pathInfo: GraphPathInfo;
  unsaved: boolean;
};

interface NodeBoxApi {
  calculators: {
    nodes: BoundsCalculator;
    edges: BoundsCalculator;
    merged: MergedBoundsCalculator;
  };
  highlight: () => void;
  setLayout: (layout: LayoutState | null) => void;
  unhighlight: () => void;
}

type NodeBoxApis = Map<BoxId, NodeBoxApi>;
type BoxObjects = Map<BoxId, Container>;

const HIT_AREA_THICKNESS = 40;
const NODE_BOX_LINE_COLOR_HIGHLIGHT = 0x323232;
const SPACE_BETWEEN_LABEL_AND_USER_DEFINED_ICON = 10;

// NIM-5159 we need to determine graph path from box id here,
// or return null in future for boxes that don't represent a path.
const getBoxPath = (boxId: BoxId): GraphPath => brand<GraphPath>(boxId);

const getBorderHitArea = (boxBounds: Bounds): IHitArea => {
  const innerArea = pipe(
    bounds.resize(boxBounds, -HIT_AREA_THICKNESS / 2),
    boundsToPIXIRectangle,
  );
  const outerArea = pipe(
    bounds.resize(boxBounds, HIT_AREA_THICKNESS / 2),
    boundsToPIXIRectangle,
  );
  return {
    contains: (x, y) => outerArea.contains(x, y) && !innerArea.contains(x, y),
  };
};

const getBoxLayoutState = (
  metadata: TopologyGraphMetadata,
  graphPath: GraphPath,
) => {
  const pathInfo = metadata.paths[graphPath] ?? null;
  return pathInfo === null
    ? null
    : {
        pathInfo,
        unsaved: metadata.nodesRepositioned.has(graphPath),
      };
};

export const nodeBoxes: GraphRendererPlugin = (pluginInterface) => {
  const boxes: NodeBoxApis = new Map();
  const boxesBounds: Map<BoxId, Bounds> = new Map();
  /**
   * Because edge itself doesn't hold information to which box it belongs (extracted from related nodes),
   * we have to remember to which box it belonged last to properly react on changes.
   */
  const edgeBoxMap = new Map<EdgeId, BoxId>();

  /**
   * This includes hidden nodes too - we need to keep track of them separately because they are excluded
   * from nodesCalculator.
   */
  const boxNodesMap = new Map<BoxId, Set<NodeId>>();
  const boxObjects: BoxObjects = new Map();

  const drawBox =
    (boxObject: Graphics, boxLabel: Container, highlight: boolean) =>
    /**
     * Draw rectangle with label around given nodes bounding box
     *
     * @return bounds of drawn box with label
     */
    (entitiesBounds: Bounds): Bounds => {
      boxObject.clear();
      const positions = getNodeBoxPositions(entitiesBounds, boxLabel);
      boxObject.lineStyle(
        NODE_BOX_LINE_WIDTH,
        highlight ? NODE_BOX_LINE_COLOR_HIGHLIGHT : NODE_BOX_LINE_COLOR,
        NODE_BOX_LINE_ALPHA,
      );
      boxObject.drawRoundedRect(
        positions.left,
        positions.top,
        positions.width,
        positions.height,
        NODE_BOX_BORDER_RADIUS,
      );
      boxObject.hitArea = getBorderHitArea({
        left: positions.left,
        top: positions.top,
        right: positions.right,
        bottom: positions.bottom,
      });
      boxLabel.position.set(positions.centerX, positions.top);
      return positions.bounds;
    };

  const drawLabel = (
    labelControlsContainer: Container,
    boxId: BoxId,
  ): BitmapText => {
    const labelText = pluginInterface.getGraphData().boxLabels[boxId] ?? boxId;
    const boxLabel = new BitmapText(labelText, {
      font: { name: NODE_BOX_LABEL_FONT, size: NODE_BOX_LABEL_FONT_SIZE },
    });
    getBitmapTextAnchor(boxLabel).y = 0.5;
    labelControlsContainer.addChild(boxLabel);
    return boxLabel;
  };

  const setupLayoutButtonTooltip = (button: Sprite, unsaved: boolean): void => {
    button.on('mouseover', (event: interaction.InteractionEvent) => {
      if (
        'offsetX' in event.data.originalEvent &&
        'offsetY' in event.data.originalEvent
      ) {
        const { width: buttonWidth, height: buttonHeight } = button.getBounds();
        pluginInterface.graphDispatch(showLayoutButtonTooltip, {
          position: {
            x: button.getGlobalPosition().x + buttonWidth / 2,
            y: button.getGlobalPosition().y - buttonHeight / 2,
            inverseY: button.getGlobalPosition().y + buttonHeight / 2,
          },
          unsaved,
        });
      }
    });
    button.on('mouseout', () => {
      pluginInterface.graphDispatch(hideTooltip, { type: 'layoutButton' });
    });
  };

  const drawLayoutButton = (
    labelControlsContainer: Container,
    boxLabel: BitmapText,
    graphPath: GraphPath,
    layout: LayoutState | null,
  ): void => {
    if (layout !== null) {
      const button = new Sprite(
        getGraphResources().pathLayoutButtons[
          layout.unsaved ? 'unsaved' : layout.pathInfo.defaultLayout
        ],
      );
      button.tint =
        !layout.unsaved && isFallbackLayout(layout.pathInfo)
          ? 0xff6666
          : 0xffffff;
      button.position.x =
        boxLabel.position.x +
        boxLabel.textWidth +
        SPACE_BETWEEN_LABEL_AND_USER_DEFINED_ICON;
      button.anchor.y = 0.5;
      button.width = 24;
      button.height = 24;
      button.interactive = true;
      button.cursor = 'pointer';
      button.on('click', () => {
        // We must postpone opening the dialog to next tick, otherwise the click will be immediately
        // consumed as click outside of the dialog which closes it.
        setTimeout(() => {
          pluginInterface.graphDispatch(showGraphLayoutDialog, {
            path: graphPath,
          });
        }, 0);
      });
      setupLayoutButtonTooltip(button, layout.unsaved);

      labelControlsContainer.addChild(button);
    }
  };

  const drawLabelControlsBackground = (
    labelControlsContainer: Container,
  ): Sprite => {
    const background = new Sprite(Texture.WHITE);

    background.width =
      labelControlsContainer.width + 2 * NODE_BOX_LABEL_PADDING;
    background.height = labelControlsContainer.height;
    background.anchor.set(0.5, 0.5);

    return background;
  };

  const drawLabelControls = (
    boxContainer: Container,
    boxId: BoxId,
    layout: LayoutState | null,
  ) => {
    const container = new Container();

    const labelControlsContainer = new Container();
    const boxLabel = drawLabel(labelControlsContainer, boxId);
    drawLayoutButton(
      labelControlsContainer,
      boxLabel,
      getBoxPath(boxId),
      layout,
    );
    const background = drawLabelControlsBackground(labelControlsContainer);

    labelControlsContainer.pivot.set(labelControlsContainer.width / 2, 0);

    container.addChild(background, labelControlsContainer);
    boxContainer.addChild(container);

    return container;
  };

  const getInitialLayoutState = (graphPath: GraphPath): LayoutState | null => {
    const metadata = pluginInterface.getGraphMetadata();
    if (!isTopologyGraphMetadata(metadata)) {
      return null;
    }
    return getBoxLayoutState(metadata, graphPath);
  };

  const createBox = (boxId: BoxId): NodeBoxApi => {
    let layout: LayoutState | null = getInitialLayoutState(getBoxPath(boxId));
    const boxContainer = new Container();
    boxContainer.zIndex = ZINDEX_BOX_BASE;
    const mergedCalculator = new MergedBoundsCalculator();
    const edgesCalculator = new BoundsCalculator();
    const nodesCalculator = new BoundsCalculator();
    mergedCalculator.addCalculator(edgesCalculator);
    mergedCalculator.addCalculator(nodesCalculator);
    let lastBounds: Option<Bounds> = option.none;

    const boxObject = new Graphics();
    let boxLabel = drawLabelControls(boxContainer, boxId, layout);
    const drawThisBox = (highlight: boolean) =>
      drawBox(boxObject, boxLabel, highlight);
    boxObject.interactive = true;
    let grabbing = false;
    defineReadonlyAccessor(boxObject, 'cursor', () => {
      if (pluginInterface.isSelectionMode()) {
        return 'default';
      }
      return grabbing ? 'grabbing' : 'grab';
    });

    const highlight = () => {
      option.map(drawThisBox(true))(lastBounds);
      pluginInterface.requestRender();
    };

    const unhighlight = () => {
      option.map(drawThisBox(false))(lastBounds);
      pluginInterface.requestRender();
    };

    const setLayout = (nextLayout: LayoutState | null) => {
      if (nextLayout !== layout) {
        layout = nextLayout;
        boxLabel.destroy();
        boxLabel = drawLabelControls(boxContainer, boxId, layout);
        option.map(drawThisBox(false))(lastBounds);
        pluginInterface.requestRender();
      }
    };

    boxObject.on('mouseover', highlight);
    boxObject.on('mouseout', unhighlight);

    boxObject.on('mousedown', (e: interaction.InteractionEvent) => {
      if (pluginInterface.isSelectionMode()) {
        return;
      }
      grabbing = true;
      const boundsBeforeDrag = mergedCalculator.getBounds();
      pipe(
        boxNodesMap,
        plainMap.get(boxId),
        option.fold(
          () =>
            stumble(
              `Trying to drag box ${boxId} but its record in boxesNodesIncludingHidden not found`,
            ),
          (nodeIds) => {
            pluginInterface.events.emit('dragNodes', {
              initialMousePosition: e.data.global.clone(),
              nodeIds: Array.from(nodeIds),
              onDragStep: ({ delta }) => {
                pipe(
                  boundsBeforeDrag,
                  option.map((b) => {
                    const newBounds = bounds.move(b, delta);
                    lastBounds = option.some(newBounds);
                    drawThisBox(true)(newBounds);
                  }),
                );
              },
              onDragEnd: () => {
                grabbing = false;
                mergedCalculator.addCalculator(edgesCalculator);
              },
            });
          },
        ),
      );
    });

    boxObject.on('rightclick', () => {
      if (layout !== null) {
        pluginInterface.graphDispatch(showGraphLayoutDialog, {
          path: getBoxPath(boxId),
        });
      }
    });

    boxContainer.addChild(boxObject);

    boxObjects.set(boxId, boxObject);

    boxContainer.addChild(boxLabel);
    pluginInterface.graphLayer.addChild(boxContainer);

    mergedCalculator.addListener('change', ({ newBounds: entitiesBounds }) => {
      lastBounds = entitiesBounds;
      pipe(entitiesBounds, option.map(drawThisBox(false)), (boxBounds) => {
        pluginInterface.events.emit('boxBoundsChange', {
          bounds: boxBounds,
          boxId,
        });
        if (option.isSome(boxBounds)) {
          boxesBounds.set(boxId, boxBounds.value);
        } else {
          boxesBounds.delete(boxId);
          boxContainer.destroy();
          boxes.delete(boxId);
          boxObjects.delete(boxId);
        }
      });
    });

    return {
      calculators: {
        edges: edgesCalculator,
        nodes: nodesCalculator,
        merged: mergedCalculator,
      },
      highlight,
      setLayout,
      unhighlight,
    };
  };

  const removeFromBounds =
    (container: 'nodes' | 'edges') =>
    (boxId: BoxId | null, entityId: NodeId | EdgeId) => {
      if (boxId !== null) {
        boxes.get(boxId)?.calculators[container].removeObjectBounds(entityId);
      }
    };

  const removeNodeFromBounds = removeFromBounds('nodes');

  const removeEdgeFromBounds = removeFromBounds('edges');

  const updateBounds =
    (container: 'nodes' | 'edges') =>
    (
      boxId: BoxId | null,
      newBounds: Option<Bounds>,
      entityId: NodeId | EdgeId,
    ) => {
      if (boxId !== null) {
        pipe(
          newBounds,
          option.fold(
            () => removeFromBounds(container)(boxId, entityId),
            (b: Bounds) => {
              const boxApi = pipe(
                boxes,
                plainMap.getOrCreate(boxId, () => createBox(boxId)),
              );
              boxApi.calculators[container].setObjectBounds(entityId, b);
            },
          ),
        );
      }
    };

  const updateNodeBounds = updateBounds('nodes');

  const updateEdgeBounds = updateBounds('edges');

  const addToNodeBoxMap = (boxId: BoxId | null, nodeId: NodeId) => {
    if (boxId === null) {
      return;
    }

    const boxNodes = boxNodesMap.get(boxId);
    if (boxNodes) {
      boxNodes.add(nodeId);
    } else {
      boxNodesMap.set(boxId, new Set([nodeId]));
    }
  };

  const removeFromNodeBoxMap = (boxId: BoxId | null, nodeId: NodeId) => {
    if (boxId === null) {
      return;
    }

    const boxNodes = boxNodesMap.get(boxId);
    if (boxNodes) {
      boxNodes.delete(nodeId);
      if (boxNodes.size === 0) {
        boxNodesMap.delete(boxId);
      }
    }
  };

  const addNode = (
    id: NodeId,
    boxId: BoxId | null,
    nodeBounds: Option<Bounds>,
  ) => {
    updateNodeBounds(boxId, nodeBounds, id);
    addToNodeBoxMap(boxId, id);
  };

  const removeNode = (nodeId: NodeId, boxId: BoxId | null) => {
    removeFromNodeBoxMap(boxId, nodeId);
    removeNodeFromBounds(boxId, nodeId);
  };

  pluginInterface.events.addListener(
    'nodeAdded',
    ({ id, nodeData, bounds: nodeBounds }) => {
      addNode(id, nodeData.boxId, nodeBounds);
    },
  );

  pluginInterface.events.addListener(
    'nodeChange',
    ({ id, nodeData, oldNodeData, bounds: newBounds }) => {
      if (nodeData.boxId !== oldNodeData.boxId) {
        removeNode(id, oldNodeData.boxId);
        addNode(id, nodeData.boxId, newBounds);
      } else {
        // it is likely that node bounds changed somehow with node change, so let's avoid calculating old bounds
        // and just do the update
        updateNodeBounds(nodeData.boxId, newBounds, id);
      }
    },
  );

  pluginInterface.events.addListener(
    'edgeBoundsChange',
    ({ edgeData, bounds: newBounds }) => {
      const { nodes } = pluginInterface.getGraphData();
      const newBoxId = getEdgeBoxId(edgeData, nodes);
      const oldBoxId = edgeBoxMap.get(edgeData.id) ?? null;

      if (
        oldBoxId !== null &&
        (option.isNone(newBounds) || oldBoxId !== newBoxId)
      ) {
        // edge removed/hidden or its box changed
        removeEdgeFromBounds(oldBoxId, edgeData.id);
        edgeBoxMap.delete(edgeData.id);
      }

      if (newBoxId !== null && option.isSome(newBounds)) {
        updateEdgeBounds(newBoxId, newBounds, edgeData.id);
        edgeBoxMap.set(edgeData.id, newBoxId);
      }
    },
  );

  pluginInterface.events.addListener('nodeDragStart', () => {
    // biome-ignore lint/complexity/noForEach: iteration over Map
    boxObjects.forEach((boxObject) => {
      boxObject.parent.zIndex = ZINDEX_BOX_DRAG;
    });
  });

  pluginInterface.events.addListener('nodeDragEnd', () => {
    // biome-ignore lint/complexity/noForEach: iteration over Map
    boxObjects.forEach((boxObject) => {
      boxObject.parent.zIndex = ZINDEX_BOX_BASE;
    });
  });

  pluginInterface.events.addListener('beforeRemoveNode', ({ id, nodeData }) => {
    removeNode(id, nodeData.boxId);
  });

  pluginInterface.events.addListener('beforeRemoveEdge', ({ id, edgeData }) => {
    const { nodes } = pluginInterface.getGraphData();
    const nodeData = nodes[edgeData.source];

    if (nodeData) {
      removeEdgeFromBounds(nodeData.boxId, id);
    }
  });

  pluginInterface.bindGraphCommandEventListener(
    'fitGraphToView',
    ({ boxId }) => {
      if (boxId) {
        pipe(
          boxesBounds,
          plainMap.get(boxId),
          option.map(fitToViewport(pluginInterface.viewport)),
        );
      }
    },
  );

  pluginInterface.bindGraphCommandEventListener('highlightBox', ({ boxId }) => {
    boxes.get(boxId)?.highlight();
  });

  pluginInterface.bindGraphCommandEventListener(
    'unhighlightBox',
    ({ boxId }) => {
      boxes.get(boxId)?.unhighlight();
    },
  );

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  pluginInterface.events.addListener('graphMetadataChange', ({ previous }) => {
    const metadata = pluginInterface.getGraphMetadata();
    const prevTopology = isTopologyGraphMetadata(previous) ? previous : null;
    if (!isTopologyGraphMetadata(metadata)) {
      if (prevTopology !== null) {
        // remove layouting info when transitioning from topology to other modes
        // biome-ignore lint/complexity/noForEach: iteration over Map
        boxes.forEach((boxApi) => {
          boxApi.setLayout(null);
        });
      }
      return;
    }
    if (prevTopology?.paths !== metadata.paths) {
      // update path layout algorithms for paths without repositioned nodes
      for (const [path, pathInfo] of getRecordEntries(metadata.paths)) {
        if (!metadata.nodesRepositioned.has(path)) {
          boxes.get(path)?.setLayout({ pathInfo, unsaved: false });
        }
      }
    }
    if (prevTopology?.nodesRepositioned !== metadata.nodesRepositioned) {
      // update paths with repositioned nodes
      boxes.forEach((boxApi, boxId) => {
        boxApi.setLayout(getBoxLayoutState(metadata, getBoxPath(boxId)));
      });
    }
  });
};
